import React, { useState, useRef, useEffect } from "react";

const INDICATORS = [
  { id: "STD;MA%1Cross", label: "MA Cross" },
  { id: "STD;DEMA", label: "Double EMA" },
  { id: "STD;TEMA", label: "Triple EMA" },
  { id: "STD;Bollinger_Bands", label: "Bollinger Bands" },
  { id: "STD;RSI", label: "RSI" },
  { id: "STD;MACD", label: "MACD" },
  { id: "STD;PSAR", label: "Parabolic SAR" },
  { id: "Volume@tv-basicstudies", label: "Volume" },
  { id: "STD;On_Balance_Volume", label: "OBV" },
  { id: "STD;CCI", label: "CCI" },
  { id: "STD;Average_True_Range", label: "ATR" },
  { id: "STD;ROC", label: "ROC" },
];

const MAX_INDICATORS = 5;

export type ChartStyle = "1" | "3";

interface ChartFilterBarProps {
  onIndicatorsChange: (studies: string[]) => void;
  onChartStyleChange: (style: ChartStyle) => void;
  isDark?: boolean;
}

export function ChartFilterBar({
  onIndicatorsChange,
  onChartStyleChange,
  isDark = false,
}: ChartFilterBarProps) {
  const [selectedIndicators, setSelectedIndicators] = useState<string[]>([]);
  const [chartStyle, setChartStyle] = useState<ChartStyle>("1");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleIndicator = (id: string) => {
    setSelectedIndicators((prev) => {
      const isSelected = prev.includes(id);
      const next = isSelected
        ? prev.filter((i) => i !== id)
        : prev.length < MAX_INDICATORS
        ? [...prev, id]
        : prev;
      onIndicatorsChange(next);
      return next;
    });
  };

  const handleChartStyle = (style: ChartStyle) => {
    setChartStyle(style);
    onChartStyleChange(style);
  };

  const atMax = selectedIndicators.length >= MAX_INDICATORS;

  return (
    <div className="flex items-center gap-2 w-full mb-2">
      <div className="vi-glass-button vi-ta-seg-group flex items-center">
        <button
          onClick={() => handleChartStyle("1")}
          className={`vi-ta-seg-btn flex items-center gap-1.5 px-3 py-1.5 text-sm transition-all ${
            chartStyle === "1" ? "vi-ta-seg-btn-active" : ""
          }`}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <rect
              x="3"
              y="4"
              width="3"
              height="6"
              rx="0.5"
              fill="currentColor"
            />
            <line
              x1="4.5"
              y1="1"
              x2="4.5"
              y2="4"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
            <line
              x1="4.5"
              y1="10"
              x2="4.5"
              y2="13"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
            <rect
              x="8"
              y="3"
              width="3"
              height="5"
              rx="0.5"
              fill="currentColor"
              opacity="0.5"
            />
            <line
              x1="9.5"
              y1="1"
              x2="9.5"
              y2="3"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
            <line
              x1="9.5"
              y1="8"
              x2="9.5"
              y2="12"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          </svg>
          Candle
        </button>
        <button
          onClick={() => handleChartStyle("3")}
          className={`vi-ta-seg-btn vi-ta-seg-divider flex items-center gap-1.5 px-3 py-1.5 text-sm transition-all border-l ${
            chartStyle === "3" ? "vi-ta-seg-btn-active" : ""
          }`}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M1 10 C3 10 4 4 7 4 C10 4 11 8 13 8"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              fill="none"
            />
            <path
              d="M1 10 C3 10 4 4 7 4 C10 4 11 8 13 8 L13 13 L1 13 Z"
              fill="currentColor"
              opacity="0.2"
            />
          </svg>
          Area
        </button>
      </div>

      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen((prev) => !prev)}
          className="vi-glass-button flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-all"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M1 3h12M3 7h8M5 11h4"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
          Indicators
          {selectedIndicators.length > 0 && (
            <span className="vi-ta-indicator-count flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-medium">
              {selectedIndicators.length}
            </span>
          )}
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
          >
            <path
              d="M2 4l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {isOpen && (
          <div className="vi-ta-indicators-dropdown absolute top-full left-0 mt-1 z-50 rounded-xl w-[calc(100vw-2rem)] max-w-[400px] sm:w-[400px] p-4">
            {atMax && (
              <div className="mb-3 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-600 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-400">
                Maximum 5 indicators selected. Deselect one to add another.
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
              {INDICATORS.map((ind) => {
                const isSelected = selectedIndicators.includes(ind.id);
                const isDisabled = !isSelected && atMax;
                return (
                  <label
                    key={ind.id}
                    className={`flex items-center gap-2.5 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${
                      isDisabled
                        ? "opacity-40 cursor-not-allowed"
                        : "hover:bg-gray-50 dark:hover:bg-white/5"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => handleIndicator(ind.id)}
                      className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 cursor-pointer"
                    />
                    <span className="text-sm select-none text-gray-700 dark:text-gray-200">
                      {ind.label}
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-white/10 flex items-center justify-between">
              <span
                className={`text-xs ${atMax ? "text-amber-500" : "text-gray-400"}`}
              >
                {selectedIndicators.length} / {MAX_INDICATORS} selected
              </span>
              {selectedIndicators.length > 0 && (
                <button
                  onClick={() => {
                    setSelectedIndicators([]);
                    onIndicatorsChange([]);
                  }}
                  className="text-xs text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {selectedIndicators.map((id) => {
          const ind = INDICATORS.find((i) => i.id === id);
          if (!ind) return null;
          return (
            <span key={id} className="vi-glass-chip text-xs">
              {ind.label}
              <button
                onClick={() => handleIndicator(id)}
                className="ml-0.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
              >
                ✕
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
}
